import { useState, useRef, useCallback, useEffect } from 'react';

export interface ToastConfig {
  duration?: number;
  type?: 'success' | 'error' | 'info' | 'warning';
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  isVisible: boolean;
}

export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // 컴포넌트 언마운트 시 모든 타이머 정리
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  const showToast = useCallback((message: string, config: ToastConfig = {}) => {
    const { duration = 3000, type = 'success' } = config;
    const id = Date.now().toString();

    const newToast: Toast = {
      id,
      message,
      type,
      isVisible: true
    };

    setToasts(prev => [...prev, newToast]);

    // 자동 숨김 타이머 설정
    if (duration > 0) {
      const timeout = setTimeout(() => {
        hideToast(id);
      }, duration);

      timeoutsRef.current.set(id, timeout);
    }

    return id;
  }, []);

  const hideToast = useCallback((id: string) => {
    // 타이머 정리
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }

    // 토스트 제거
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'success', duration });
  }, [showToast]);

  const showError = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'error', duration });
  }, [showToast]);

  const showInfo = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'info', duration });
  }, [showToast]);

  const showWarning = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'warning', duration });
  }, [showToast]);

  const clearAll = useCallback(() => {
    // 모든 타이머 정리
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();

    // 모든 토스트 제거
    setToasts([]);
  }, []);

  return {
    toasts,
    showToast,
    hideToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    clearAll
  };
};